import React, { useState, useRef, useEffect } from 'react';

// Country code to flag emoji
function countryCodeToFlag(code) {
  if (!code || code.length !== 2) return '🌐';
  const offset = 127397;
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => c.charCodeAt(0) + offset));
}

export default function GeoInfo({ geoLocation, dnsAddresses }) {
  if (!geoLocation && (!dnsAddresses || dnsAddresses.length === 0)) return null;

  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [geoLocation, dnsAddresses, isExpanded]);

  const hasGeo = geoLocation && !geoLocation.error && geoLocation.country;
  const ip = geoLocation?.ip || (dnsAddresses && dnsAddresses[0]) || '—';
  const flag = hasGeo ? countryCodeToFlag(geoLocation.countryCode) : '🌐';

  // Summary for collapsed state
  const summaryParts = [];
  if (ip && ip !== '—') summaryParts.push(ip);
  if (hasGeo) {
    const loc = [geoLocation.city, geoLocation.countryCode].filter(Boolean).join(', ');
    if (loc) summaryParts.push(`${flag} ${loc}`);
    if (geoLocation.isp) summaryParts.push(geoLocation.isp);
  }

  return (
    <div className={`geo-info-card collapsible-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="geo-info-header collapsible-card-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="geo-info-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="2" y1="12" x2="22" y2="12"/>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          </svg>
        </div>
        <h3 className="geo-info-title">IP & Geolocalização</h3>
        {!isExpanded && summaryParts.length > 0 && (
          <div className="collapsible-card-summary">
            {summaryParts.map((part, i) => (
              <span key={i} className="collapsible-summary-chip">{part}</span>
            ))}
          </div>
        )}
        <button className="collapsible-card-toggle" aria-label={isExpanded ? 'Recolher' : 'Expandir'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`collapsible-chevron ${isExpanded ? 'rotated' : ''}`}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      </div>

      <div
        className="collapsible-card-content"
        style={{ height: isExpanded ? contentHeight : 0 }}
      >
        <div ref={contentRef} className="collapsible-card-inner">
          <div className="geo-info-body">
        {/* IP Address */}
        <div className="geo-info-row geo-ip-row">
          <div className="geo-info-label">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="2"/>
              <path d="M7 7h0M12 7h0M17 7h0M7 12h0M12 12h0M17 12h0M7 17h0M12 17h0M17 17h0"/>
            </svg>
            IP Address
          </div>
          <div className="geo-info-value geo-ip-value">
            <code>{ip}</code>
            {dnsAddresses && dnsAddresses.length > 1 && (
              <span className="geo-ip-extra">+{dnsAddresses.length - 1} more</span>
            )}
          </div>
        </div>

        {hasGeo ? (
          <>
            {/* Country */}
            <div className="geo-info-row">
              <div className="geo-info-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                  <line x1="4" y1="22" x2="4" y2="15"/>
                </svg>
                País
              </div>
              <div className="geo-info-value">
                <span className="geo-flag">{flag}</span>
                <span>{geoLocation.country}</span>
                <span className="geo-country-code">{geoLocation.countryCode}</span>
              </div>
            </div>

            {/* City / Region */}
            {(geoLocation.city || geoLocation.region) && (
              <div className="geo-info-row">
                <div className="geo-info-label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  Localização
                </div>
                <div className="geo-info-value">
                  {[geoLocation.city, geoLocation.region].filter(Boolean).join(', ')}
                  {geoLocation.zip && <span className="geo-zip">{geoLocation.zip}</span>}
                </div>
              </div>
            )}

            {/* ISP */}
            {geoLocation.isp && (
              <div className="geo-info-row">
                <div className="geo-info-label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                  ISP
                </div>
                <div className="geo-info-value">{geoLocation.isp}</div>
              </div>
            )}

            {/* Organization */}
            {geoLocation.org && geoLocation.org !== geoLocation.isp && (
              <div className="geo-info-row">
                <div className="geo-info-label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                  Organização
                </div>
                <div className="geo-info-value">{geoLocation.org}</div>
              </div>
            )}

            {/* AS Number */}
            {geoLocation.as && (
              <div className="geo-info-row">
                <div className="geo-info-label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                  </svg>
                  ASN
                </div>
                <div className="geo-info-value geo-asn">{geoLocation.as}</div>
              </div>
            )}

            {/* Timezone */}
            {geoLocation.timezone && (
              <div className="geo-info-row">
                <div className="geo-info-label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  Timezone
                </div>
                <div className="geo-info-value">{geoLocation.timezone}</div>
              </div>
            )}

            {/* Hosting indicator */}
            {geoLocation.hosting && (
              <div className="geo-info-row">
                <div className="geo-info-label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                    <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                    <line x1="6" y1="6" x2="6.01" y2="6"/>
                    <line x1="6" y1="18" x2="6.01" y2="18"/>
                  </svg>
                  Hosting
                </div>
                <div className="geo-info-value">
                  <span className="geo-hosting-badge">Data Center / Cloud Hosting</span>
                </div>
              </div>
            )}

            {/* Coordinates */}
            {geoLocation.lat && geoLocation.lon && (
              <div className="geo-info-row geo-coords-row">
                <div className="geo-info-label">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  Coordenadas
                </div>
                <div className="geo-info-value geo-coords">
                  <span>{geoLocation.lat.toFixed(4)}°</span>
                  <span className="geo-coords-sep">,</span>
                  <span>{geoLocation.lon.toFixed(4)}°</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="geo-info-row">
            <div className="geo-info-label">Status</div>
            <div className="geo-info-value geo-unavailable">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              Geolocalização indisponível
            </div>
          </div>
        )}
      </div>

      {/* Multiple IPs */}
      {dnsAddresses && dnsAddresses.length > 1 && (
        <div className="geo-multi-ip">
          <div className="geo-multi-ip-label">Todos os IPs resolvidos:</div>
          <div className="geo-multi-ip-list">
            {dnsAddresses.map((addr, i) => (
              <code key={i} className="geo-multi-ip-item">{addr}</code>
            ))}
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
